import { useMemo } from 'react';

import type {
  UseChatMessageStateParams,
  UseChatMessageStateReturn,
} from '../types/hooks';

export function useChatMessageState({
  messages,
  isThinking,
  isStreaming,
  recommendations,
  errorMessage,
  activeStreamEventSeq,
  activeAgentMessageId,
}: UseChatMessageStateParams): UseChatMessageStateReturn {
  // Business rule: Logo appears only on the last agent message.
  // Search backwards to find most recent non-user message efficiently.
  const lastAgentMessageIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (!messages[i]?.isUser) return i;
    }
    return -1;
  }, [messages]);

  // Business rule: Recommendations and errors should not show while thinking or streaming.
  const isBusy = isThinking || isStreaming;

  // Business rules: Logo visibility depends on conversation state.
  // Recommendations/errors hide during busy state.
  // Last agent message shows logo during streaming (with pulsing avatar) but not during thinking.
  const showLogoOnRecommendation = !isBusy && Boolean(recommendations);
  const showLogoOnError = !isBusy && !recommendations && Boolean(errorMessage);
  const showLogoOnLastAgentMessage =
    (!isThinking && !recommendations && !errorMessage) || isStreaming;

  // Business rule: Show thinking indicator until we have actual text to display.
  // Show when:
  // 1. isThinking is true (user sent message, waiting for response)
  // 2. isStreaming but no content yet in the active streaming message
  // 3. Last message is from user and there's no agent response after it (fallback for race conditions)
  const activeStreamingMessage = activeAgentMessageId
    ? messages.find((m) => m.id === activeAgentMessageId)
    : null;
  const hasActiveStreamingContent = Boolean(activeStreamingMessage && activeStreamingMessage.text.length > 0);
  
  // Fallback check: if last message is user and no agent response exists after it
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const hasNoAgentResponse = lastMessage?.isUser === true;
  const hasEmptyAgentResponse = Boolean(lastMessage && !lastMessage.isUser && lastMessage.text.length === 0);
  const waitingForAgentAfterUserMessage = hasNoAgentResponse || hasEmptyAgentResponse;
  
  // Invariant: isAwaitingAgentResponse captures all states where the UX should
  // communicate "Eva is working" even if no agent text is visible yet. This
  // flag drives both the thinking indicator and avatar animation.
  const isAwaitingAgentResponse = Boolean(
    isThinking || 
    (isStreaming && !hasActiveStreamingContent) ||
    waitingForAgentAfterUserMessage
  );

  const scrollDependencies = useMemo(
    () => [
      messages.length,
      isThinking,
      recommendations,
      errorMessage,
      activeStreamEventSeq,
    ],
    [messages.length, isThinking, recommendations, errorMessage, activeStreamEventSeq],
  );

  return {
    lastAgentMessageIndex,
    showLogoOnRecommendation,
    showLogoOnError,
    showLogoOnLastAgentMessage,
    isAwaitingAgentResponse,
    scrollDependencies,
  };
}
